import { useEffect } from 'react'

// EOS-5229
// The soft keyboard can cover the field being typed into - reported for Create
// Password / Confirm Password, which sit at the end of the signup step, in the
// Tri-Rail app WebView.
//
// Browsers normally handle this themselves once the keyboard changes the
// viewport, and they do it better than we can, so this hook is only a safety
// net: it measures whether the focused field is *still* covered after the
// viewport has settled, and only then moves it, by the smallest distance that
// works. When the page cannot scroll that far - the field is last in the form
// and the page is already at the bottom - it adds exactly the missing pixels of
// scroll room and takes them away again on blur.
//
// The viewport reacts to the keyboard in one of three ways, and the difference
// matters because getBoundingClientRect() is relative to the layout viewport:
//   Android    the layout viewport shrinks, so window.innerHeight already *is*
//              the top of the keyboard,
//   iOS        only the visual viewport shrinks, so visualViewport.height is,
//   WebView    neither changes; nothing to measure, so the height is estimated.
// Reading the wrong one is what made an earlier version of this hook add a large
// phantom gap on Android and scroll past the content.

// Types that raise a keyboard. A <select> opens a native picker instead, and
// padding the page for a keyboard that never appears just leaves a gap.
const KEYBOARD_INPUT_TYPES = [
    'text', 'password', 'email', 'tel', 'number', 'search', 'url',
    'date', 'datetime-local', 'month', 'time', 'week',
]
// Below this, a viewport change is browser chrome (the URL bar), not a keyboard.
const MIN_KEYBOARD_HEIGHT = 100

const isKeyboardField = (el) => {
    if (!el || typeof el.matches !== 'function') return false
    if (el.matches('textarea')) return true
    if (!el.matches('input')) return false
    return KEYBOARD_INPUT_TYPES.includes((el.type || 'text').toLowerCase())
}

const useKeyboardAwareForm = (containerRef, {
    // Gap kept between the focused field and the top of the keyboard.
    bottomGap = 16,
    // Gap kept above the field, so it is never pushed off the top of the screen.
    topOffset = 12,
    // Fraction of the viewport the keyboard is assumed to cover, used ONLY in a
    // WebView whose viewport has never once reacted to the keyboard.
    assumedKeyboardRatio = 0.45,
    // ms - long enough for the viewport to resize before we assume it will not.
    settleDelay = 400,
} = {}) => {
    useEffect(() => {
        const container = containerRef?.current
        if (!container) return

        // Only where a soft keyboard exists. `pointer: coarse` reports the
        // *primary* pointer, so phones and tablets match but a touchscreen
        // laptop - which has no soft keyboard to dodge - does not.
        const hasSoftKeyboard = window.matchMedia
            ? window.matchMedia('(pointer: coarse)').matches
            : navigator.maxTouchPoints > 0
        if (!hasSoftKeyboard) return

        let focusedField = null
        // Viewport height with no keyboard up, used to spot a layout resize.
        let idleHeight = window.innerHeight
        let idleWidth = window.innerWidth
        // Set once we have seen this viewport respond to a keyboard at all -
        // after that, measurements are trusted and nothing is assumed.
        let viewportReacts = false
        let addedRoom = 0
        let alignTimer = null
        let releaseTimer = null

        // Where the keyboard starts, in layout-viewport coordinates.
        const visibleBottom = () => {
            const viewport = window.visualViewport
            const visualShrink = viewport ? window.innerHeight - viewport.height : 0
            if (visualShrink >= MIN_KEYBOARD_HEIGHT) {
                viewportReacts = true
                return viewport.height
            }
            const layoutShrink = idleHeight - window.innerHeight
            if (layoutShrink >= MIN_KEYBOARD_HEIGHT) {
                viewportReacts = true
                return window.innerHeight
            }
            // Nothing shrank: either no keyboard is up, or this is a WebView that
            // never resizes for one. Only assume the latter while we have no
            // evidence that this viewport ever reacts.
            return viewportReacts ? window.innerHeight : window.innerHeight * (1 - assumedKeyboardRatio)
        }

        const addRoom = (pixels) => {
            addedRoom += pixels
            container.style.paddingBottom = `${addedRoom}px`
            // Force the new scroll height to be picked up before we scroll into it.
            void container.offsetHeight
        }

        const releaseRoom = () => {
            if (!addedRoom) return
            addedRoom = 0
            container.style.removeProperty('padding-bottom')
        }

        const align = () => {
            if (!focusedField || !document.body.contains(focusedField)) return

            const rect = focusedField.getBoundingClientRect()
            const keyboardTop = visibleBottom()
            const overflow = rect.bottom - (keyboardTop - bottomGap)
            // Not covered - the browser has already dealt with it. Do nothing.
            if (overflow <= 0) return

            const delta = Math.min(overflow, rect.top - topOffset)
            if (delta < 4) return

            const scrollable = document.documentElement.scrollHeight - window.innerHeight
            const room = scrollable - window.scrollY
            if (room < delta) addRoom(Math.ceil(delta - room))

            // Instant, not smooth: a smooth scroll runs over several frames and
            // gets cancelled by the keyboard animation or a stray touch, which
            // would leave the field half covered.
            window.scrollBy({ top: delta, behavior: 'auto' })
        }

        const scheduleAlign = (delay) => {
            clearTimeout(alignTimer)
            alignTimer = setTimeout(align, delay)
        }

        const onFocusIn = (e) => {
            if (!isKeyboardField(e.target)) return
            clearTimeout(releaseTimer)
            focusedField = e.target
            scheduleAlign(settleDelay)
        }

        const onFocusOut = () => {
            // Deferred: moving between two fields fires focusout before focusin,
            // and releasing the scroll room in between would jolt the page.
            clearTimeout(releaseTimer)
            releaseTimer = setTimeout(() => {
                if (isKeyboardField(document.activeElement) && container.contains(document.activeElement)) return
                focusedField = null
                clearTimeout(alignTimer)
                releaseRoom()
            }, 150)
        }

        // The viewport resizing is the reliable signal that the keyboard opened;
        // re-check against the real measurement as soon as it does.
        const onViewportChange = () => {
            if (focusedField) {
                scheduleAlign(120)
                return
            }
            if (window.innerWidth !== idleWidth) {
                // Rotation or a real window resize - start the baseline over.
                idleWidth = window.innerWidth
                idleHeight = window.innerHeight
                return
            }
            // Keep the tallest height seen at the same width. A keyboard can
            // still be up here (it closes just after blur), and taking a
            // shrunken reading as the baseline would hide the next keyboard.
            idleHeight = Math.max(idleHeight, window.innerHeight)
        }

        container.addEventListener('focusin', onFocusIn)
        container.addEventListener('focusout', onFocusOut)
        window.addEventListener('resize', onViewportChange)
        window.visualViewport?.addEventListener('resize', onViewportChange)

        return () => {
            clearTimeout(alignTimer)
            clearTimeout(releaseTimer)
            container.removeEventListener('focusin', onFocusIn)
            container.removeEventListener('focusout', onFocusOut)
            window.removeEventListener('resize', onViewportChange)
            window.visualViewport?.removeEventListener('resize', onViewportChange)
            releaseRoom()
        }
    }, [containerRef, bottomGap, topOffset, assumedKeyboardRatio, settleDelay])
}

export default useKeyboardAwareForm
